import { useCallback, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Divider } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import PageLayout from "../../components/Layout/PageLayout";
import TextField from "../../components/Inputs/TextField";
import PathConstants from "../../routes/pathConstants";

import { useParkingPass } from "../../hooks/parking-pass";

import ArrowForward from "../../assets/Icons/ArrowForward";
import ArrowBack from "../../assets/Icons/ArrowBack";
import { postPrepaid } from "../../services/prepaid";
import Modal from "../../components/Layout/Modal";
import Alert from "../../assets/Icons/Alert";
import Phone from "../../assets/Icons/Phone";
import Receipt from "../../assets/Icons/Receipt";
import ErrorModal from "../../components/Layout/ErrorModal";
import { normalizeBarcode } from "../../utils/formatBarcode";
import cacheKeys from "../../constants/cache";
import BarcodeQRScanner from "../../components/Buttons/BarcodeQRScanner";
import { useTheme } from "../../hooks/useTheme";

const PrepaidTicket = () => {
  const [passAlreadyPurchased, setPassAlreadyPurchased] = useState(false);
  const [prepaidIsLoading, setPrepaidIsLoading] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: parkingPass } = useParkingPass();
  const contactInfo = queryClient.getQueryData(["contactInfo"]);
  const prepaid = queryClient.getQueryData(["prepaid"]);
  const welcome = queryClient.getQueryData(["welcome"])
  const printedTicket = welcome?.skin?.flowConfig?.printedTicket;

  const qrCodeCache = window.sessionStorage.getItem(cacheKeys.qrCode);

  const showDash = useMemo(() => {
    if (printedTicket?.input?.dashStyle) {
      return printedTicket.input.dashStyle === "mlb";
    }
    return true;
  }, [printedTicket])

  useEffect(() => {
    if (!qrCodeCache) {
      setIsRedirecting(true);
      window.location.replace(PathConstants.ERROR);
    }

    if (!contactInfo || !welcome) {
      setIsRedirecting(true);
      queryClient.setQueryData(["parkingPass"], { passBarcode: "" });
      window.location.replace(`${PathConstants.WELCOME}?qr=${qrCodeCache}`);
    }
  }, [qrCodeCache, contactInfo, welcome, queryClient]);

  const mutation = useMutation({
    mutationFn: (date) => postPrepaid(parkingPass.passBarcode, false, "", date),
    onSuccess: (data) => {
      queryClient.setQueryData(["prepaid"], data);
      if (data?.validTicket) {
        navigate(PathConstants.CHECKOUT_COMPLETE);
      } else {
        if (data?.failReason === "rateLotMismatch") {
          navigate(PathConstants.LOT_MISMATCH_PREPAID);
        } else if (data?.failReason === "alreadyPaid") {
          setPassAlreadyPurchased(true);
        } else if (data.failReason) {
          setShowErrorModal(true);
        }
      }
    },
    onError: (error) => {
      console.error(error);
    },
    onSettled: () => {
      setPrepaidIsLoading(false);
    },
  });

  const handleOnClickReceipt = useCallback(() => {
    navigate(PathConstants.RECEIPT);
  }, [navigate]);

  const handleOnSubmitPass = useCallback(() => {
    const date = new Date();
    setPrepaidIsLoading(true);
    mutation.mutate(date);
  }, [mutation]);

  const handleOnClickBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleOnChangeBarcode = useCallback(
    (e) => {
      queryClient.setQueryData(["parkingPass"], {
        passBarcode: normalizeBarcode(e.target.value.toUpperCase(), showDash),
      });
    },
    [queryClient, showDash]
  );

  const handleOnClick = useCallback(
    (path) => {
      navigate(path);
    },
    [navigate]
  );

  const continueEnabled = useMemo(
    () => !!parkingPass?.passBarcode,
    [parkingPass?.passBarcode]
  );

  if (isRedirecting) {
    return null;
  }

  return (
    <PageLayout title={printedTicket?.header || "Parking Pass"} withCancel isLoading={prepaidIsLoading}>
      <ErrorModal
        open={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        failReason={prepaid?.failReason}
      />
      <Modal
        open={passAlreadyPurchased}
        onClose={() => setPassAlreadyPurchased(false)}
      >
        <div>
          <span className="mx-auto">
            <Alert />
            <div>
              <div className="text-center mt-4 font-bold text-lg">
                Parking Already Purchased
              </div>
              <div className="text-center mt-4">
                Parking for this vehicle has already been purchased.
              </div>
              <div
                className="mt-4"
                style={{ marginLeft: "-1.5rem", marginRight: "-1.5rem" }}
              >
                <Divider />
              </div>
              <div>
                <div className="mt-4 flex">
                  <Button
                    size="medium"
                    variant="contained"
                    onClick={() => handleOnClick()}
                    color="primary"
                    style={{
                      fontWeight: 700,
                      height: "2.5rem",
                      flexGrow: 1,
                      marginRight: "1rem",
                      width: "45%",
                    }}
                    startIcon={<Phone />}
                  >
                    Need Help?
                  </Button>
                  <Button
                    size="medium"
                    variant="contained"
                    onClick={handleOnClickReceipt}
                    color="secondary"
                    style={{
                      fontWeight: 700,
                      flexGrow: 1,
                      height: "2.5rem",
                      width: "45%",
                    }}
                    disabled={false}
                    startIcon={<Receipt fill="var(--neutral-white-color)" />}
                  >
                    View Receipt
                  </Button>
                </div>
              </div>
            </div>
          </span>
        </div>
      </Modal>
      <div className="text-secondary leading-4 text-sm mx-2">
        {printedTicket?.description ||
          "Scan or enter the 12 digit number located under the QR or barcode on your ticket."}
      </div>
      <div className="my-5">
        <BarcodeQRScanner
          onScanSuccess={(data) =>
            handleOnChangeBarcode({ target: { value: data } })
          }
          onScanFailure={() => console.log("Scan failed")}
        />
        <TextField
          value={parkingPass?.passBarcode || ""}
          onChange={handleOnChangeBarcode}
          label="Pass Number"
          placeholder={printedTicket?.input?.placeholder || (showDash ? "1234-12345678" : "123412345678")}
          inputProps={{ maxLength: 64 }}
          // add margin top to the text field
          className="mt-4"
        />
      </div>
      <div className="text-left text-secondary leading-4 text-sm font-bold">
        <span>By continuing you agree to our</span>{" "}
        <a
          href={PathConstants.TERMS_OF_USE}
          className="underline text-secondary font-bold"
          target="_blank"
          rel="noopener noreferrer"
        >
          Terms of Use
        </a>{" "}
        <span>and</span>{" "}
        <a
          href={PathConstants.PRIVACY_POLICY}
          className="underline text-secondary font-bold"
          target="_blank"
          rel="noopener noreferrer"
        >
          Privacy Policy
        </a>
      </div>
      <div className="mt-4">
        <Button
          size="large"
          variant="contained"
          onClick={handleOnClickBack}
          color="secondary"
          style={{
            fontWeight: 700,
            width: "30%",
            height: "3rem",
            position: "inline-block",
            marginRight: "1rem",
          }}
          startIcon={<ArrowBack fill="var(--neutral-white-color)" />}
        >
          Back
        </Button>
        <Button
          size="large"
          variant="contained"
          onClick={handleOnSubmitPass}
          color="primary"
          style={{ fontWeight: 700, width: "65%", height: "3rem" }}
          endIcon={
            <ArrowForward
              fill={
                !continueEnabled
                  ? "rgba(0, 0, 0, 0.26)"
                  : "var(--secondary-color)"
              }
            />
          }
          disabled={!continueEnabled}
        >
          Agree & Continue
        </Button>
      </div>
    </PageLayout>
  );
};

export default PrepaidTicket;
